import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const caseStudies = [
    {
      title: 'Financial Institution AI Risk Control System',
      subtitle: 'Deployment & Optimization Case',
      description: 'Deployed AI risk control system for a major financial institution, achieving 99.9% system availability, risk identification accuracy of 98.5%, 300% processing speed improvement, and over 40% annual cost savings.',
      logo: 'Financial Services'
    },
    {
      title: 'Retail Enterprise Smart Recommendations',
      subtitle: 'Performance Optimization Case',
      description: 'Optimized AI recommendation engine for retail chain, reducing response time from 2 seconds to 200 milliseconds, improving recommendation accuracy by 35%, increasing user conversion rate by 28%, and reducing system operation costs by 50%.',
      logo: 'Retail Innovation'
    },
    {
      title: 'Manufacturing Predictive Maintenance',
      subtitle: 'Full Lifecycle Management Case',
      description: 'Deployed predictive maintenance AI system for manufacturing enterprise, achieving 95% equipment failure prediction accuracy, reducing unplanned downtime by 70%, lowering maintenance costs by 45%, and improving production efficiency by 25%.',
      logo: 'Manufacturing Tech'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <div className={`text-3xl font-bold tracking-tight ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                <span className="bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">AI</span>
                <span className={scrolled ? 'text-gray-900' : 'text-white'}>MSP</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>Core Services</a>
              <a href="#advantages" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>Advantages</a>
              <a href="#process" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>Process</a>
              <a href="#cases" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>Case Studies</a>
              <a href="#contact" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>Contact</a>
              <a href="#contact" className="px-6 py-2.5 bg-teal-500 text-white text-sm font-medium rounded-full hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">Get Started</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3d9021b903b789a15dbb6237958420a3.jpg" 
            alt="AI Technology Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm mb-8">
              <div className="w-2 h-2 bg-teal-400 rounded-full mr-2 animate-pulse"></div>
              <span className="text-white text-sm font-medium">AI Managed Service Provider</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Empower Your<br />
              <span className="text-6xl md:text-8xl bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">AI Journey</span><br />
              End-to-End Services
            </h1>
            
            <p className="text-lg md:text-xl text-gray-200 mb-12 max-w-3xl mx-auto leading-relaxed">
              From model deployment to continuous optimization, fully managed services<br />
              AIMSP provides professional AI lifecycle management solutions
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#services" className="px-8 py-4 bg-transparent border-2 border-teal-400 text-white text-base font-semibold rounded-lg hover:bg-teal-400/20 transition-all shadow-lg shadow-teal-400/30 whitespace-nowrap cursor-pointer">
                Explore Services
              </a>
              <a href="#cases" className="text-white text-base font-medium hover:text-teal-400 transition-colors whitespace-nowrap cursor-pointer">
                View Cases →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services Section */}
      <section id="services" className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-teal-400 text-sm font-semibold tracking-widest mb-4">OUR CORE SERVICES</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">AI Lifecycle Management Solutions</h2>
            <div className="w-48 h-0.5 bg-gradient-to-r from-transparent via-teal-400 to-transparent mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-box-3-line',
                title: 'AI Model Deployment & Maintenance',
                description: 'Professional model deployment services ensuring stable AI system operation. Including model optimization, version management, automated deployment processes, and 7×24 monitoring and maintenance to guarantee high system availability.',
                features: ['Model Optimization', 'Version Control', 'Auto Deployment', 'Real-time Monitoring']
              },
              {
                icon: 'ri-database-2-line',
                title: 'Data Management & Governance',
                description: 'Build comprehensive data management systems, from data collection, cleaning, annotation to storage. Ensure data quality, comply with privacy protection and regulatory requirements, providing high-quality data support for AI models.',
                features: ['Data Cleaning', 'Quality Management', 'Privacy Protection', 'Compliance Audit']
              },
              {
                icon: 'ri-dashboard-line',
                title: 'Performance Optimization & Monitoring',
                description: 'Continuously monitor AI system performance metrics, promptly identify and resolve performance bottlenecks. Through intelligent tuning technology, improve model inference speed and accuracy, reduce operational costs, ensure optimal performance.',
                features: ['Performance Monitoring', 'Smart Tuning', 'Cost Optimization', 'Alert System']
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Security & Compliance',
                description: 'Provide comprehensive security protection solutions, including data encryption, access control, security audits. Ensure AI systems comply with industry standards and regulatory requirements, protecting core business assets and user privacy.',
                features: ['Data Encryption', 'Access Control', 'Security Audit', 'Compliance Certification']
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Technical Support & Training',
                description: 'Professional technical support team ready to respond quickly to customer needs. Regular technical training to help enterprise teams master AI system operation skills, achieve knowledge transfer and capability enhancement.',
                features: ['7×24 Support', 'Technical Training', 'Knowledge Base', 'Best Practices']
              },
              {
                icon: 'ri-line-chart-line',
                title: 'Continuous Optimization & Upgrade',
                description: 'Based on business data and user feedback, continuously optimize AI models and system architecture. Regular evaluation of technology trends, provide upgrade recommendations, ensure enterprise AI capabilities remain industry-leading.',
                features: ['Model Iteration', 'Architecture Optimization', 'Tech Upgrade', 'Effect Evaluation']
              }
            ].map((service, index) => (
              <div key={index} className="group relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 border border-teal-400/20 hover:border-teal-400/60 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:shadow-teal-400/20 cursor-pointer">
                <div className="flex items-center justify-center w-16 h-16 bg-teal-400/10 rounded-2xl mb-6 group-hover:bg-teal-400/20 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-400`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                <p className="text-gray-400 text-base leading-relaxed mb-6">{service.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {service.features.map((feature, idx) => (
                    <span key={idx} className="px-3 py-1 bg-teal-400/10 text-teal-400 text-xs rounded-full">{feature}</span>
                  ))}
                </div>
                
                <div className="text-teal-400 text-sm font-medium group-hover:translate-x-2 transition-transform inline-block cursor-pointer">
                  Learn More →
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Advantages Section */}
      <section id="advantages" className="py-24 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-teal-400/20">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20AI%20technology%20infrastructure%20with%20server%20clusters%20neural%20network%20visualization%20and%20data%20flow%20diagrams%20in%20dark%20blue%20theme%20professional%20tech%20architecture%20with%20glowing%20connections%20and%20geometric%20patterns%20futuristic%20data%20center%20concept%20high%20tech%20atmosphere%20clean%20minimalist%20design&width=800&height=600&seq=tech-adv-001&orientation=landscape" 
                  alt="AI Technology Infrastructure"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                  <div className="text-white text-2xl font-bold">Serving Over <span className="text-teal-400">200+</span> Enterprises</div>
                </div>
              </div>
            </div>

            <div>
              <div className="inline-block px-4 py-2 bg-teal-400/10 text-teal-400 text-sm font-semibold rounded-full mb-6">
                WHY CHOOSE AIMSP
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
                Focus on AI Operations<br />
                <span className="bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">Unleash Innovation Potential</span>
              </h2>
              
              <div className="space-y-8 mb-12">
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Eliminate Technical Barriers</h3>
                  <p className="text-gray-400 text-base leading-relaxed">
                    No need to build a professional AI team, AIMSP provides end-to-end managed services. From model selection, deployment to optimization, our expert team handles all technical details, allowing you to focus on core business innovation.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Optimize Cost Structure</h3>
                  <p className="text-gray-400 text-base leading-relaxed">
                    Adopt flexible subscription model, avoid high upfront investment and labor costs. Through intelligent resource scheduling and performance optimization, we help clients reduce AI operational costs by an average of 40%, improving ROI.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">7×24 Professional Support</h3>
                  <p className="text-gray-400 text-base leading-relaxed">
                    Round-the-clock technical support team ready to respond, quickly addressing system anomalies and business needs. Average response time less than 15 minutes, ensuring your AI system runs stably and business remains unaffected.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-8 pt-8 border-t border-gray-800">
                <div>
                  <div className="text-4xl font-bold text-teal-400 mb-2">99.9%</div>
                  <div className="text-sm text-gray-400">System Uptime</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-teal-400 mb-2">40%</div>
                  <div className="text-sm text-gray-400">Cost Savings</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-teal-400 mb-2">15min</div>
                  <div className="text-sm text-gray-400">Response Time</div>
                </div>
              </div>

              <a href="#contact" className="inline-flex items-center mt-8 px-8 py-4 bg-transparent border-2 border-teal-400 text-white font-semibold rounded-lg hover:bg-teal-400 hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer">
                Explore Solutions
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Process Section */}
      <section id="process" className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-teal-400 text-sm font-semibold tracking-widest mb-4">SERVICE PROCESS</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Professional Service Process</h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              From requirement analysis to continuous optimization, we provide comprehensive AI managed services, ensuring excellence at every stage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Requirement Assessment',
                description: 'Deep understanding of business scenarios, evaluate AI application needs, develop customized solutions',
                icon: 'ri-search-line'
              },
              {
                step: '02',
                title: 'System Deployment',
                description: 'Rapid deployment of AI models and infrastructure, configure monitoring and security protection systems',
                icon: 'ri-rocket-line'
              },
              {
                step: '03',
                title: 'Operations & Monitoring',
                description: '7×24 system monitoring, timely handling of anomalies, ensuring stable operation',
                icon: 'ri-eye-line'
              },
              {
                step: '04',
                title: 'Continuous Optimization',
                description: 'Continuously optimize model performance based on data feedback, enhance business value',
                icon: 'ri-refresh-line'
              }
            ].map((process, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-teal-400/20 hover:border-teal-400/60 transition-all duration-300 h-full">
                  <div className="text-6xl font-bold text-teal-400/20 mb-4">{process.step}</div>
                  
                  <div className="flex items-center justify-center w-14 h-14 bg-teal-400/10 rounded-xl mb-6">
                    <i className={`${process.icon} text-2xl text-teal-400`}></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-4">{process.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{process.description}</p>
                </div>
                
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-2xl text-teal-400"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Carousel Section */}
      <section id="cases" className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-teal-400 text-sm font-semibold tracking-widest mb-4">SUCCESS STORIES</div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Client Success Stories</h2>
            <p className="text-gray-400 text-lg max-w-3xl mx-auto">
              We help clients across industries achieve successful AI implementation, creating significant business value
            </p>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl overflow-hidden border border-teal-400/20">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src={`https://readdy.ai/api/search-image?query=professional%20business%20success%20case%20study%20visualization%20with%20data%20analytics%20dashboard%20charts%20and%20graphs%20in%20modern%20office%20environment%20clean%20corporate%20style%20with%20blue%20and%20teal%20accent%20colors%20technology%20innovation%20concept%20high%20quality%20professional%20photography&width=800&height=600&seq=case-${currentSlide}&orientation=landscape`}
                    alt={caseStudies[currentSlide].title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-8 left-8 px-4 py-2 bg-white/90 backdrop-blur-sm text-gray-900 text-sm font-semibold rounded-lg">
                    SUCCESS STORY
                  </div>
                  <div className="absolute bottom-8 left-8 right-8">
                    <div className="text-white text-3xl md:text-4xl font-bold leading-tight">
                      {caseStudies[currentSlide].title}
                    </div>
                    <div className="text-teal-400 text-xl font-semibold mt-2">
                      {caseStudies[currentSlide].subtitle}
                    </div>
                  </div>
                </div>

                <div className="p-12 flex flex-col justify-center">
                  <div className="mb-8">
                    <div className="text-gray-500 text-sm font-semibold mb-2">{caseStudies[currentSlide].logo}</div>
                    <h3 className="text-3xl font-bold text-white mb-6">{caseStudies[currentSlide].title}</h3>
                    <p className="text-gray-400 text-base leading-relaxed">
                      {caseStudies[currentSlide].description}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      {[0, 1, 2].map((index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentSlide(index)}
                          className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                            currentSlide === index ? 'bg-teal-400 w-8' : 'bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <div className="flex gap-4">
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev - 1 + 3) % 3)}
                        className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-teal-400 text-white rounded-full transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line"></i>
                      </button>
                      <button
                        onClick={() => setCurrentSlide((prev) => (prev + 1) % 3)}
                        className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-teal-400 text-white rounded-full transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-gray-950 to-gray-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-teal-500 to-blue-600 rounded-3xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-12 lg:p-16 flex flex-col justify-center">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  Ready to Start Your<br />AI Transformation?
                </h2>
                <p className="text-white/90 text-lg mb-8 leading-relaxed">
                  Contact our expert team to get customized AI managed solutions. We will provide professional consulting services to help you achieve rapid AI implementation.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center text-white">
                    <div className="flex items-center justify-center w-10 h-10 bg-white/20 rounded-lg mr-4">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <div className="text-sm text-white/70">Email</div>
                      <div className="font-semibold">wan@aimsp.cc</div>
                    </div>
                  </div>
                </div>

                <a href="mailto:wan@aimsp.cc" className="inline-flex items-center justify-center px-8 py-4 bg-white text-teal-600 font-bold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
                  Contact Us Now
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>

              <div className="relative h-96 lg:h-auto">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20professional%20business%20team%20collaboration%20in%20bright%20office%20space%20with%20technology%20and%20AI%20innovation%20diverse%20professionals%20working%20together%20clean%20corporate%20environment%20natural%20lighting%20contemporary%20workspace%20design%20positive%20atmosphere%20high%20quality%20business%20photography&width=800&height=800&seq=contact-001&orientation=squarish" 
                  alt="Contact AIMSP"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="text-3xl font-bold tracking-tight mb-6">
                <span className="bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">AI</span>
                <span className="text-white">MSP</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                AIMSP is Singapore's leading AI managed service provider, dedicated to providing professional AI lifecycle management solutions for enterprises.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-700 rounded-full text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-700 rounded-full text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 flex items-center justify-center border border-gray-700 rounded-full text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-github-fill"></i>
                </a>
                <a href="mailto:wan@aimsp.cc" className="w-10 h-10 flex items-center justify-center border border-gray-700 rounded-full text-gray-400 hover:border-teal-400 hover:text-teal-400 transition-colors cursor-pointer">
                  <i className="ri-mail-line"></i>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Core Services</h3>
              <ul className="space-y-3">
                <li><a href="#services" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Model Deployment</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Data Management</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Performance Optimization</a></li>
                <li><a href="#services" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Technical Support</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Company</h3>
              <ul className="space-y-3">
                <li><a href="#advantages" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">About Us</a></li>
                <li><a href="#cases" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Success Stories</a></li>
                <li><a href="#process" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Service Process</a></li>
                <li><a href="#contact" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Contact Us</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Contact</h3>
              <ul className="space-y-3">
                <li className="text-gray-400 text-sm">
                  <strong className="text-white">Email:</strong><br />
                  wan@aimsp.cc
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-gray-400 text-sm">
              © 2025 AIMSP PTE. LTD. All rights reserved.
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-400 text-sm hover:text-teal-400 transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}